import React, { useRef, useState } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useFormik } from 'formik';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { EModalState } from 'components/LoginModal/LoginModal.types';

import { useResetLinkGuard } from '../../hooks/useResetLinkGuard';
import { required } from '../../utils/errorsTextHelper';
import {
  getFieldDescription,
  getFieldState,
  getPasswordVisibilityType,
  getRecaptchaSiteKey,
  getRevealPasswordIconName
} from '../../utils/recaptchaFormHelpers';
import { EToasterStatus, showToast } from '../../utils/showToast';
import { ResetLinkParamsValidation } from '../../utils/validation/resetLinkParams';
import Typography from '../Typography/Typography';

import { FinishAccountFormProps } from './FinishAccountForm.types';

import './style.scss';

YupPassword(yup);

function getFormikInitialEmail(
  resetLinkValidation: ResetLinkParamsValidation
): string {
  if (resetLinkValidation.isValid) {
    return resetLinkValidation.normalizedEmail;
  }
  return '';
}

const finishAccountFormSchema = yup.object().shape({
  password1: yup
    .string()
    .label('Password')
    .password()
    .required(required('Password')),
  password2: yup
    .string()
    .label('Password')
    .oneOf([yup.ref('password1'), null], 'Passwords must match')
    .required()
});

export const FinishAccountForm: React.FC<FinishAccountFormProps> = ({
  setNewPassword,
  setModalState
}) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const resetLinkValidation = useResetLinkGuard({ setModalState });
  const isResetLinkValid = resetLinkValidation.isValid;

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formikInitialEmail = getFormikInitialEmail(resetLinkValidation);

  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    submitForm,
    isValid
  } = useFormik({
    initialValues: {
      email: formikInitialEmail,
      password1: '',
      password2: ''
    },
    validationSchema: finishAccountFormSchema,
    onSubmit: async (formValues) => {
      if (!resetLinkValidation.isValid) {
        return;
      }

      const recaptchaToken = await recaptchaRef.current?.executeAsync();
      if (!recaptchaToken) {
        showToast({
          message: 'Please complete the reCAPTCHA verification',
          status: EToasterStatus.ERROR
        });
        return;
      }
      recaptchaRef.current?.reset();

      setIsLoading(true);
      const passwordResetPayload = {
        email: resetLinkValidation.normalizedEmail,
        rawNewPassword: formValues.password1,
        token: resetLinkValidation.token
      };
      setNewPassword({
        passwordResetPayload,
        recaptchaToken,
        onComplete: (success: boolean) => {
          setIsLoading(false);
          if (success) {
            setModalState(EModalState.LOGIN);
          }
        }
      });
    }
  });

  const password1FieldState = getFieldState(errors.password1, !!touched.password1);
  const password2FieldState = getFieldState(errors.password2, !!touched.password2);

  const password1VisibilityType = getPasswordVisibilityType(showPassword1);
  const password2VisibilityType = getPasswordVisibilityType(showPassword2);

  const sendButtonDisabled = !isValid || !dirty;

  const password1DescriptionText = getFieldDescription(errors.password1, !!touched.password1);
  const password2DescriptionText = getFieldDescription(errors.password2, !!touched.password2);

  if (!isResetLinkValid) {
    return null;
  }

  return (
    <div className="w-full">
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={getRecaptchaSiteKey()}
        size="invisible"
        className="hidden"
      />
      <div className="flex flex-col gap-y-2 text-center mb-6">
        <Typography tag="h4">Create your Password</Typography>
        <Typography className="text-p-s text-base-mutedForeground">
          Choose your passwords for logging in to AppCharge's Dashboard.
        </Typography>
      </div>
      <form
        className="flex flex-col gap-y-4"
        onSubmit={(e: React.SyntheticEvent) => {
          e.preventDefault();
        }}
        autoComplete="off"
      >
        <Input
          label="Email"
          placeholder="Email"
          name="email"
          value={resetLinkValidation.normalizedEmail}
          disabled
          autoComplete="off"
        />
        <div>
          <Input
            rightIcon={
              <FontAwesomeIcon
                onClick={() => setShowPassword1((prev) => !prev)}
                icon={getRevealPasswordIconName(showPassword1)}
              />
            }
            type={password1VisibilityType}
            label="New Password"
            placeholder="New Password"
            name="password1"
            value={values.password1}
            onChange={handleChange}
            onBlur={handleBlur}
            state={password1FieldState}
            description={password1DescriptionText}
            autoComplete="off"
          />
          <Typography className="mt-2 text-p-s text-base-mutedForeground">
            Your password must be at least 8 characters long and contain at
            least one capital letter (A-Z), one lowercase letter (a-z), one
            numeral (0-9) and at least one special symbol (e.g., !, @, #, $, %,
            etc.).
          </Typography>
        </div>
        <Input
          label="Confirm Password"
          placeholder="Confirm Password"
          name="password2"
          rightIcon={
            <FontAwesomeIcon
              onClick={() => setShowPassword2((prev) => !prev)}
              icon={getRevealPasswordIconName(showPassword2)}
            />
          }
          type={password2VisibilityType}
          value={values.password2}
          onChange={handleChange}
          onBlur={handleBlur}
          state={password2FieldState}
          description={password2DescriptionText}
          autoComplete="off"
        />
        <Button
          disabled={sendButtonDisabled}
          type="submit"
          onClick={submitForm}
          className="w-full"
          isLoading={isLoading}
        >
          Send
        </Button>
      </form>
      <Typography
        className="text-p-s cursor-pointer hover:opacity-80 underline text-center mt-9"
        onClick={() => setModalState(EModalState.LOGIN)}
      >
        Go back to login page
      </Typography>
    </div>
  );
};
